import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch } from 'react-redux';

import { CopyIcon, DownloadIcon } from '../../../../assets/images/icons';
import {
  ATTRIBUTION_SOURCE_OPTIONS,
  AttributionLinkSource,
  formatAttributionSourceOptionLabel,
} from '../../../../common/constants/attribution-link-source';
import { FormMode } from '../../../../common/constants/enums';
import { Button } from '../../../../common/ui/button';
import { Divider } from '../../../../common/ui/divider';
import { Notification } from '../../../../common/ui/notification';
import { Select } from '../../../../common/ui/select';
import { SingleDatePicker } from '../../../../common/ui/single-date-picker';
import { TextField } from '../../../../common/ui/text-field';
import { utilCopyToClipboard } from '../../../../common/utils/utilCopyToClipboard';
import { asinRegex } from '../../../../common/validationSchemas';
import { useAppSelector } from '../../../../hooks';
import {
  GenerateTemporaryLinkActionPayload,
  generateTemporaryLinksSlice,
} from '../../../../store/reducers/temporary-links/generate-temporary-links-slice';
import { PublisherSelect } from '../../../pages/components/publisher-select';
import styles from './partner-boost-links.module.scss';

const PartnerBoostLinks = () => {
  const dispatch = useDispatch();
  const {
    error,
    isLoading,
    links,
  } = useAppSelector(({ generateTemporaryLinks }) => generateTemporaryLinks);

  const [asinsInput, setAsinsInput] = useState<string>('');

  const [requestData, setRequestData] = useState<
  { expiredAt: Date, publisherId: number, region: string, source: AttributionLinkSource }
  >({
    expiredAt: null,
    publisherId: 0,
    region: 'us',
    source: AttributionLinkSource.PartnerBoost,
  });

  const [asins, setAsins] = useState<string[]>([]);

  const extractIdentifiers = (text: string) => {
    const matches = text.match(asinRegex) || [];
    setAsins(matches);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setAsinsInput(value);
    extractIdentifiers(value);
  };

  const onGenerateLinks = () => {
    const requestBody: GenerateTemporaryLinkActionPayload = {
      asins,
      publisher_id: requestData.publisherId || 0,
      region: requestData.region,
      source: requestData.source,
      ...(requestData.expiredAt ? { expired_at: format(requestData.expiredAt, 'yyyy-MM-dd') } : {}),
    };

    dispatch(generateTemporaryLinksSlice.actions.resetState());
    dispatch(generateTemporaryLinksSlice.actions.generate({ payload: requestBody }));
  };

  useEffect(() => () => {
    dispatch(generateTemporaryLinksSlice.actions.resetState());
  }, []);

  return <>
    <div className="w-75 d-flex column-gap-20 mb-20">
      <PublisherSelect
        className={styles.publisher_select}
        formMode={FormMode.SEARCH}
        onPublisherChange={({ country, id }) => {
          setRequestData((prevState) => ({
            ...prevState,
            publisherId: id,
            region: country || 'us',
          }));
        }}
      />
      <Select
        className={styles.source_select}
        formatOptionLabel={formatAttributionSourceOptionLabel}
        isSearchable={false}
        onChange={(option) => setRequestData((prevState) => ({
          ...prevState,
          source: option.value,
        }))}
        options={ATTRIBUTION_SOURCE_OPTIONS}
        value={ATTRIBUTION_SOURCE_OPTIONS.find((o) => o.value === requestData.source)}
      />
      <SingleDatePicker
        buttonProps={{}}
        disabled={isLoading}
        onChange={(day) => setRequestData((prevState) => ({
          ...prevState,
          expiredAt: day,
        }))}
        value={requestData.expiredAt}
      >
        Select date (optional)
      </SingleDatePicker>
    </div>
    <TextField
      className="w-75"
      disabled={isLoading}
      onChange={handleInputChange}
      placeholder="ASINs..."
      rows={10}
      value={asinsInput}
      variant="secondary"
    />
    <div className="w-75 d-flex justify-end mt-20">
      <Button
        className="w-15"
        disabled={!asins?.length}
        loading={isLoading}
        onClick={onGenerateLinks}
        variant="primary"
      >
        Generate
      </Button>
    </div>
    <div className="flex-column w-75 raw-gap-20 mt-20 mb-30">
      {(links?.updated?.length || links?.created?.length) && !isLoading
        && <>
          <Divider className="w-100" variant="secondary" />
          <div className="w-100 d-flex justify-end mb-20">
            <Button iconStart={<DownloadIcon />} variant="secondary">
              <CSVLink
                className={styles.button_title}
                data={[...links?.created || [], ...links?.updated || []]} // Массив данных
                filename="generated_links.csv"
                headers={[{
                  key: 'asin',
                  label: 'ASIN',
                }, {
                  key: 'link',
                  label: 'Link',
                }]}
              >
                Export CSV
              </CSVLink>
            </Button>
          </div>
        </>}

      {links?.created?.length > 0 && !isLoading && (<>
        <span className={styles.section_title}>Created: </span>
        <div className={styles.links}>
          {links.created.map((link) =>
            <div key={link.asin} className={styles.link}>
              <span
                className={styles.link_title}
                onClick={() => utilCopyToClipboard(link?.asin)}
              >
                {link.asin}
              </span>

              <span
                className={styles.link_value}
                /* eslint-disable-next-line react/no-danger */
                onClick={() => window.open(link.link)}
              >
                {link.link}
              </span>

              <Button
                iconStart={<CopyIcon />}
                onClick={() => utilCopyToClipboard(link?.link)}
                outlined
                variant="secondary"
              />
            </div>)}
        </div>
      </>)}

      {links?.updated?.length > 0 && !isLoading && (<>
        <span className={styles.section_title}>Updated: </span>
        <div className={styles.links}>
          {links.updated.map((link) =>
            <div key={link.asin} className={styles.link}>
              <span
                className={styles.link_title}
                onClick={() => utilCopyToClipboard(link?.asin)}
              >
                {link.asin}
              </span>

              <span
                className={styles.link_value}
                /* eslint-disable-next-line react/no-danger */
                onClick={() => window.open(link.link)}
              >
                {link.link}
              </span>

              <Button
                iconStart={<CopyIcon />}
                onClick={() => utilCopyToClipboard(link?.link)}
                outlined
                variant="secondary"
              />
            </div>)}
        </div>
      </>)}

      {links?.notFoundAsins?.length > 0 && !isLoading && <>
        <span className={styles.section_title}>Not found ASINs: </span>
        <span className={styles.not_found_asins}>{links?.notFoundAsins.join(', ')}</span>
      </>
      }
      {!isLoading && error && <Notification fullWidth message={error?.message} type="error" />}
    </div>
  </>;
};

export { PartnerBoostLinks };
